import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Mic, MicOff, Video, PhoneOff, Bot, Loader2, Play, ChevronLeft,
  Sparkles, AlertTriangle, CheckCircle2, RotateCcw, Clock, X, Calendar,
} from "lucide-react";
import { getApiUrlsWithFallback } from "@/config/api";
import { INTERVIEW_TIME_LIMIT } from "../demoData";
import DemoResult from "./DemoResult";
import { useNavigate } from "react-router-dom";

// Brand ink used for the dark AI-interviewer panel (matches landing dark bands)
const INK = "hsl(164 33% 7%)";

// We ask for a WORK email — block the common free/personal providers.
const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.in", "hotmail.com", "outlook.com",
  "live.com", "icloud.com", "aol.com", "proton.me", "protonmail.com", "gmx.com",
  "mail.com", "yandex.com", "zoho.com", "rediffmail.com",
]);
const isValidEmailFormat = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const validateWorkEmail = (e) => {
  if (!isValidEmailFormat(e)) return "Please enter a valid email address.";
  const domain = e.split("@")[1].toLowerCase();
  if (FREE_EMAIL_DOMAINS.has(domain)) return "Please use your work email, not a personal address.";
  return "";
};

const deriveFirstName = (raw) => {
  const s = (raw || "").trim();
  if (!s) return "";
  let base = s.includes("@") ? s.split("@")[0] : s;
  base = (base.split(/[._\-\s]+/).filter(Boolean)[0] || base).replace(/[^a-zA-Z]/g, "");
  if (!base) return "";
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
};

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Pull readable text out of a Realtime event payload.
function extractText(obj) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  for (const k of ["transcript", "delta", "text", "output_text"]) {
    if (typeof obj[k] === "string") return obj[k];
  }
  return "";
}

const CAP_MESSAGES = {
  email_cap: "You've reached the daily limit of practice interviews for this email. Please try again tomorrow.",
  ip_cap: "This network has reached today's demo limit. Please try again later.",
  email_required: "A valid work email is required to start.",
  server_misconfigured: "The live interview isn't available right now. Please try again later.",
  mint_failed: "We couldn't start the AI session right now. Please try again in a moment.",
  network_error: "Network issue starting the session. Please check your connection and retry.",
};

export default function RealtimeInterviewDemo({ persona, onBack }) {
  const roleTitle = persona.role;
  const [phase, setPhase] = useState("setup"); // setup | starting | interview | scoring | report
  const [connected, setConnected] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(INTERVIEW_TIME_LIMIT);
  const [result, setResult] = useState(null);
  const [currentAIResponse, setCurrentAIResponse] = useState("");
  const [, setConvTick] = useState(0);

  // Lead modal
  const [showLead, setShowLead] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadError, setLeadError] = useState("");
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const streamRef = useRef(null);       // camera + mic
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioElRef = useRef(null);
  const conversationRef = useRef([]);   // [{ role, text }]
  const nameRef = useRef("");
  const emailRef = useRef("");
  const endingRef = useRef(false);
  const aiRespondingRef = useRef(false);
  const timerRef = useRef(null);
  const startedRef = useRef(false);     // guard against double session start

  const pushMessage = (role, text) => {
    const t = (text || "").trim();
    if (!t) return;
    const last = conversationRef.current[conversationRef.current.length - 1];
    if (last && last.role === role && last.text === t) return; // dedupe
    conversationRef.current = [...conversationRef.current, { role, text: t }];
    setConvTick((n) => n + 1);
  };

  // ── teardown ──────────────────────────────────────────────────────────────
  const stopEverything = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { dcRef.current && dcRef.current.close(); } catch {}
    try {
      if (pcRef.current) { pcRef.current.getSenders().forEach((s) => s.track?.stop()); pcRef.current.close(); }
    } catch {}
    try {
      if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current.srcObject = null; audioElRef.current.remove(); }
    } catch {}
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    pcRef.current = null; dcRef.current = null; audioElRef.current = null;
  }, []);

  useEffect(() => () => stopEverything(), [stopEverything]);

  // The camera <video> only exists in the "interview" phase, so attach the
  // captured stream once that screen has mounted (setting srcObject earlier,
  // during "starting", hit a not-yet-mounted element → black preview).
  useEffect(() => {
    if (phase === "interview" && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  // ── scoring at the end ─────────────────────────────────────────────────────
  const scoreInterview = useCallback(async () => {
    setPhase("scoring");
    const conversation = conversationRef.current
      .filter((m) => m.text && m.text.trim())
      .map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", text: m.text }));
    const numQuestions = Math.max(1, (persona.questions?.length || 5) - 1);
    const payload = { role: roleTitle, num_questions: numQuestions, conversation };

    let data = null;
    for (const url of getApiUrlsWithFallback("/api/demo/interview/score")) {
      try {
        const res = await fetch(url, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
        });
        if (!res.ok) continue;
        const json = await res.json();
        data = json;
        if (json && json.ok) break;
      } catch (e) { /* try next */ }
    }
    setResult(data && data.ok ? data : { ok: false });
    setPhase("report");
  }, [persona.questions, roleTitle]);

  const endInterview = useCallback(() => {
    if (endingRef.current) return;
    endingRef.current = true;
    setConnected(false);
    setAiSpeaking(false);
    stopEverything();
    scoreInterview();
  }, [stopEverything, scoreInterview]);

  const endRef = useRef(endInterview);
  useEffect(() => { endRef.current = endInterview; }, [endInterview]);

  // ── 3-minute countdown ─────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "interview") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          if (!endingRef.current) endRef.current();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [phase]);

  // ── Realtime (OpenAI WebRTC) ───────────────────────────────────────────────
  const startRealtime = useCallback(async () => {
    // 1) mint a gated ephemeral key from our backend
    const payload = {
      persona: persona.id,
      role: roleTitle,
      gender: persona.gender,
      interviewer: persona.name,
      name: nameRef.current,
      email: emailRef.current,
      questions: persona.questions,
    };
    let json = null, capReason = null;
    for (const url of getApiUrlsWithFallback("/api/demo/session")) {
      try {
        const res = await fetch(url, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => null);
        if (res.ok && body && body.ok) { json = body; break; }
        if (body && body.reason) capReason = body.reason;
      } catch (e) { /* try next */ }
    }
    if (!json) {
      // Out of free interviews for this email — permanent "already_taken", or
      // the legacy daily "email_cap" — show the professional "book a demo"
      // popup instead of a cold error message.
      if (capReason === "already_taken" || capReason === "email_cap") {
        stopEverything();
        endingRef.current = false;
        setPhase("setup");
        setShowLimitModal(true);
        return;
      }
      setError(CAP_MESSAGES[capReason] || "We couldn't start the live interview right now. Please try again.");
      stopEverything();
      endingRef.current = false;
      setPhase("setup");
      return;
    }

    const EPHEMERAL_KEY =
      json?.session?.client_secret?.value ?? json?.session?.client_secret ?? json?.client_secret?.value ?? null;
    if (!EPHEMERAL_KEY) {
      setError("We couldn't start the AI session (no key). Please try again.");
      setPhase("setup");
      return;
    }

    try {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // AI audio playback
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      audioEl.addEventListener("playing", () => { setAiSpeaking(true); aiRespondingRef.current = true; });
      audioEl.addEventListener("play", () => { setAiSpeaking(true); aiRespondingRef.current = true; });
      audioEl.addEventListener("pause", () => { setAiSpeaking(false); aiRespondingRef.current = false; });
      audioEl.addEventListener("ended", () => { setAiSpeaking(false); aiRespondingRef.current = false; });
      pc.ontrack = (ev) => { if (ev.streams && ev.streams[0]) audioEl.srcObject = ev.streams[0]; };

      // send our mic to the model
      const micTrack = streamRef.current?.getAudioTracks?.()[0];
      if (micTrack) pc.addTrack(micTrack, streamRef.current);

      // events channel
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onopen = () => {
        setConnected(true);
        setPhase("interview");
        // kick off: the session already carries the interviewer instructions
        try { dc.send(JSON.stringify({ type: "response.create" })); } catch (e) {}
      };
      dc.onmessage = (ev) => {
        let data = null;
        try { data = JSON.parse(ev.data); } catch { return; }
        const type = (data?.type || "").toString();

        if (/response\.(output_audio_transcript|output_text|audio_transcript)\.delta$/.test(type)) {
          const d = data?.delta || extractText(data);
          if (d) setCurrentAIResponse((p) => p + d);
          aiRespondingRef.current = true;
          return;
        }
        if (/response\.(output_audio_transcript|output_text|audio_transcript)\.done$/.test(type)) {
          const finalText = (data?.transcript || data?.text || extractText(data) || "").trim();
          if (finalText) pushMessage("assistant", finalText);
          setCurrentAIResponse("");
          aiRespondingRef.current = false;
          return;
        }
        if (type === "conversation.item.input_audio_transcription.completed") {
          pushMessage("user", (data?.transcript || "").trim());
          return;
        }
        if (type === "response.done") { setCurrentAIResponse(""); aiRespondingRef.current = false; return; }
      };

      // SDP handshake with OpenAI Realtime (GA endpoint; model is in the token)
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sdpResp = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: { Authorization: `Bearer ${EPHEMERAL_KEY}`, "Content-Type": "application/sdp" },
        body: offer.sdp,
      });
      if (!sdpResp.ok) {
        setError("The AI connection failed to establish. Please try again.");
        stopEverything();
        endingRef.current = false;
        setPhase("setup");
        return;
      }
      const answer = await sdpResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } catch (err) {
      setError("Something went wrong starting the live interview. Please try again.");
      stopEverything();
      endingRef.current = false;
      setPhase("setup");
    }
  }, [persona, roleTitle, stopEverything]);

  // ── start: camera+mic, then realtime ───────────────────────────────────────
  const beginInterview = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setError("");
    setPhase("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: "user" },
        // Explicit constraints so the browser's own noise suppression is on;
        // autoGainControl off would otherwise boost quiet background up to VAD.
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      endingRef.current = false;
      conversationRef.current = [];
      setTimeLeft(INTERVIEW_TIME_LIMIT);
      // The camera preview is attached by the phase-driven effect below, once
      // the interview screen (and its <video>) has actually mounted.
      await startRealtime();
    } catch (err) {
      setError("We couldn't access your camera/microphone. Please allow access and try again.");
      setPhase("setup");
    } finally {
      startedRef.current = false;
    }
  };

  const submitLead = () => {
    const name = leadName.trim();
    const email = leadEmail.trim();
    if (!name) { setLeadError("Please enter your name."); return; }
    const emailErr = validateWorkEmail(email);
    if (emailErr) { setLeadError(emailErr); return; }
    setLeadError("");
    nameRef.current = deriveFirstName(name);
    emailRef.current = email;
    saveLead(name, email);
    setShowLead(false);
    beginInterview();
  };

  const saveLead = (name, email) => {
    const body = JSON.stringify({ name, email, role: roleTitle, persona: persona.id });
    (async () => {
      for (const url of getApiUrlsWithFallback("/api/demo/lead")) {
        try {
          const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body });
          if (res.ok) { const j = await res.json().catch(() => null); if (j && j.ok) break; }
        } catch (e) { /* try next */ }
      }
    })();
  };

  const toggleMic = () => {
    const track = streamRef.current?.getAudioTracks?.()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  };

  // ═══════════════════════════ SETUP SCREEN ═══════════════════════════════
  const limitModal = showLimitModal ? (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowLimitModal(false)} />
      <div className="relative w-full max-w-md rounded-2xl p-7 shadow-2xl text-white text-center" style={{ backgroundColor: INK }}>
        <button onClick={() => setShowLimitModal(false)} className="absolute right-4 top-4 text-white/50 hover:text-white transition" aria-label="Close">
          <X size={20} />
        </button>
        <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow mb-4 mx-auto">
          <Sparkles size={26} className="text-primary-foreground" />
        </div>
        <h2 className="text-xl font-bold mb-2">Ready to see the full platform?</h2>
        <p className="text-white/70 text-sm leading-relaxed mb-6">
          You've used your free AI-interview preview for this email. Book a quick personalized
          demo to see how SearchJob.ai screens, scores and shortlists every applicant for your
          team — automatically.
        </p>
        <button
          onClick={() => navigate("/hirewithus/book-demo")}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition"
        >
          <Calendar size={16} /> Book a demo
        </button>
        <button
          onClick={() => setShowLimitModal(false)}
          className="mt-3 w-full text-white/50 hover:text-white text-sm transition"
        >
          Maybe later
        </button>
      </div>
    </div>
  ) : null;

  if (phase === "setup" || phase === "starting") {
    return (
      <div className="max-w-3xl mx-auto py-8">
        {onBack && (
          <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition mb-6">
            <ChevronLeft size={16} /> All interviews
          </button>
        )}

        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-accent text-primary flex items-center justify-center text-xl font-bold ring-2 ring-border">
            {(persona.name?.[0] || "") + (roleTitle?.split(" ").slice(-1)[0]?.[0] || "")}
          </div>
          <div>
            <div className="flex items-center gap-2 text-primary text-xs font-semibold mb-1">
              <Sparkles size={14} /> LIVE AI INTERVIEW
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
              Interview with {persona.name}
            </h1>
            <div className="text-sm text-muted-foreground">{roleTitle} · {persona.category}</div>
          </div>
        </div>

        <p className="text-muted-foreground mb-8 text-base leading-relaxed">
          {persona.name} is a real AI interviewer — it listens, understands your answers, asks follow-ups,
          and will repeat a question if you ask. Just speak naturally. You have 3 minutes, and our AI scores
          your answers at the end.
        </p>

        {error && (
          <div className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive p-3 text-sm mb-6">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-secondary/40 p-4 mb-6 text-sm text-muted-foreground">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span className="flex items-center gap-2"><Video size={16} className="text-primary" /> Camera preview</span>
            <span className="flex items-center gap-2"><Mic size={16} className="text-primary" /> Real-time voice conversation</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-primary" /> 3-minute limit</span>
            <span className="flex items-center gap-2"><Bot size={16} className="text-primary" /> AI evaluation</span>
          </div>
        </div>

        <button
          onClick={() => { setLeadError(""); setShowLead(true); }}
          disabled={phase === "starting"}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold shadow-lg hover:bg-primary/90 transition disabled:opacity-60"
        >
          {phase === "starting" ? (
            <><Loader2 size={18} className="animate-spin" /> Connecting…</>
          ) : (
            <><Play size={18} /> Start interview</>
          )}
        </button>
        <p className="text-xs text-muted-foreground mt-3">
          Your camera stays on your device. Your microphone audio is streamed to the AI interviewer to conduct the interview.
        </p>

        {showLead && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowLead(false)} />
            <div className="relative w-full max-w-md rounded-2xl p-7 shadow-2xl text-white" style={{ backgroundColor: INK }}>
              <button onClick={() => setShowLead(false)} className="absolute right-4 top-4 text-white/50 hover:text-white transition" aria-label="Close">
                <X size={20} />
              </button>
              <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow mb-4">
                <Bot size={22} className="text-primary-foreground" />
              </div>
              <h2 className="text-xl font-bold mb-1">Before you meet {persona.name}</h2>
              <p className="text-white/60 text-sm mb-5">Add your details to start your live AI interview.</p>

              <label className="block text-xs font-semibold text-white/70 mb-1.5">Your name</label>
              <input
                type="text" value={leadName} onChange={(e) => setLeadName(e.target.value)}
                placeholder="e.g. Priya Nair"
                className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40 mb-4"
              />
              <label className="block text-xs font-semibold text-white/70 mb-1.5">Work email</label>
              <input
                type="email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitLead(); }}
                placeholder="you@company.com"
                className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              />
              {leadError && (
                <div className="flex items-start gap-2 text-sm text-red-300 mt-3">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" /> <span>{leadError}</span>
                </div>
              )}
              <button
                onClick={submitLead}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition"
              >
                <Play size={16} /> Start interview
              </button>
              <p className="text-[11px] text-white/40 mt-3 text-center">
                We'll only use your email to share results and contact you about SearchJob.ai.
              </p>
            </div>
          </div>
        )}
        {limitModal}
      </div>
    );
  }

  // ═══════════════════════════ SCORING SCREEN ═════════════════════════════
  if (phase === "scoring") {
    return (
      <div className="max-w-md mx-auto py-24 text-center">
        <Loader2 size={40} className="animate-spin text-primary mx-auto mb-4" />
        <div className="text-lg font-semibold text-foreground">Evaluation in Progress</div>
        <div className="flex items-center justify-center gap-1.5 mt-3" aria-hidden="true">
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
        <p className="text-sm text-muted-foreground mt-4">
          Our AI is reading your transcript and scoring it — this usually takes a few seconds.
        </p>
      </div>
    );
  }

  // ═══════════════════════════ REPORT SCREEN ══════════════════════════════
  if (phase === "report") {
    return (
      <div className="py-4">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-success text-sm font-semibold mb-1">
              <CheckCircle2 size={16} /> INTERVIEW COMPLETE
            </div>
            <h1 className="text-2xl font-bold text-foreground">Your AI evaluation report</h1>
          </div>
          <div className="flex items-center gap-2">
            {onBack && (
              <button onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent transition">
                <ChevronLeft size={16} /> Try another
              </button>
            )}
            <button onClick={() => setShowLimitModal(true)} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition">
              <RotateCcw size={16} /> Run again
            </button>
          </div>
        </div>
        <DemoResult result={result} conversation={conversationRef.current} />
        {limitModal}
      </div>
    );
  }

  // ═══════════════════════════ INTERVIEW SCREEN ═══════════════════════════
  const lowTime = timeLeft <= 30;
  const messages = conversationRef.current;
  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
          <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
            {persona.name?.[0]}
          </span>
          <span>{persona.name} <span className="text-muted-foreground">· {roleTitle}</span></span>
          <span className="ml-1 flex items-center gap-1.5 text-xs">
            <span className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-amber-500 animate-pulse"}`} />
            <span className="text-muted-foreground">{connected ? "Connected" : "Connecting…"}</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums ${lowTime ? "text-destructive" : "text-foreground"}`}>
            <Clock size={15} /> {fmt(timeLeft)}
          </span>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-destructive">
            <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" /> REC
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Candidate video */}
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-video shadow-lg">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="rounded-md bg-black/60 backdrop-blur px-2.5 py-1 text-xs font-medium text-white">You</span>
            {!micOn && (
              <span className="rounded-md bg-destructive/80 px-2 py-1 text-xs font-medium text-white flex items-center gap-1">
                <MicOff size={12} /> Muted
              </span>
            )}
          </div>
        </div>

        {/* AI interviewer */}
        <div className="relative rounded-2xl overflow-hidden aspect-video shadow-lg flex flex-col items-center justify-center p-6" style={{ backgroundColor: INK }}>
          <div className={`relative mb-4 ${aiSpeaking ? "scale-105" : ""} transition-transform`}>
            <div className={`absolute inset-0 rounded-full bg-primary/40 ${aiSpeaking ? "animate-ping" : ""}`} />
            <div className="relative w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <Bot size={38} className="text-primary-foreground" />
            </div>
          </div>
          <div className="text-white/60 text-xs font-semibold tracking-wide mb-1">{persona.name.toUpperCase()} · AI INTERVIEWER</div>
          <div className="text-white text-sm font-medium">
            {aiSpeaking ? "Speaking…" : connected ? "Listening — answer naturally" : "Connecting…"}
          </div>
        </div>
      </div>

      {/* Live transcript */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-5 max-h-64 overflow-y-auto">
        <div className="text-xs font-semibold text-muted-foreground mb-3">LIVE TRANSCRIPT</div>
        {messages.length === 0 && !currentAIResponse && (
          <p className="text-sm text-muted-foreground italic">{persona.name} will greet you and begin shortly…</p>
        )}
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "assistant" ? "" : "text-right"}>
              <div className="text-[11px] text-muted-foreground mb-0.5">{m.role === "assistant" ? persona.name : "You"}</div>
              <div className={`inline-block px-3 py-2 rounded-lg text-sm leading-relaxed max-w-[90%] ${
                m.role === "assistant" ? "bg-secondary/60 text-foreground" : "bg-primary text-primary-foreground"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {currentAIResponse && (
            <div>
              <div className="text-[11px] text-muted-foreground mb-0.5">{persona.name}</div>
              <div className="inline-block px-3 py-2 rounded-lg text-sm leading-relaxed max-w-[90%] bg-secondary/60 text-foreground border border-primary/30">
                {currentAIResponse}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={toggleMic}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition"
        >
          {micOn ? <Mic size={16} /> : <MicOff size={16} className="text-destructive" />}
          {micOn ? "Mute" : "Unmute"}
        </button>

        <button
          onClick={endInterview}
          className="inline-flex items-center gap-2 rounded-full border border-destructive/40 text-destructive px-4 py-2.5 text-sm font-medium hover:bg-destructive/10 transition"
        >
          <PhoneOff size={16} /> End &amp; score
        </button>
      </div>
    </div>
  );
}
